import React from "react";
import { Link } from "react-router-dom";
import SideBar from "./SideBar";
import TopBar from "./TopBar";
import { useTranslation } from "react-i18next";
import { DashboardSummary } from "../types/ai";
import { appLocale, fmtDate } from "./aces/Shared";

interface Props {
  mainLoading: boolean;
  allUserStats: any;
  summary: DashboardSummary | null;
  isPlatform: boolean;
}

const Kpi: React.FC<{ value: number | null | undefined; label: string; scope: string; to?: string; alert?: boolean }> = ({ value, label, scope, to, alert }) => {
  if (value === null) return null; // the user may not see this area
  const body = (
    <>
      <div className="value">{value ?? 0}</div>
      <div className="label">{label}</div>
      <div className="scope">{scope}</div>
    </>
  );
  const className = `aces-kpi ${alert && (value ?? 0) > 0 ? "alert" : ""}`;
  return to ? <Link to={to} className={className}>{body}</Link> : <div className={className}>{body}</div>;
};

const Home = ({ allUserStats, summary, isPlatform }: Props) => {
  const { t } = useTranslation();
  const month = new Intl.DateTimeFormat(appLocale(), { month: "long", year: "numeric" }).format(new Date());
  const current = t("aces.dash.current");
  const usersScope = isPlatform ? t("aces.dash.platform") : t("aces.dash.allUsers");

  return (
    <div className="__dashboard">
      <div className="__dashboard__wrapper">
        <SideBar />
        <div className="__dashboardRightSide">
          <TopBar />
          <div className="__dashboardContent">
            <div className="__pageHeading">{t("home.overview")}</div>

            <div className="aces-kpis mt-3">
              <Kpi value={allUserStats?.checklistSubmittedThisMonth} label={t("home.uploaded_docs_this_month")} scope={month} to={isPlatform ? undefined : "/submittedChecklist"} />
              {summary && <Kpi value={summary.reportsSubmittedToday} label={t("aces.dash.today")} scope={new Intl.DateTimeFormat(appLocale(), { dateStyle: "medium" }).format(new Date())} to="/submittedChecklist" />}
              <Kpi value={allUserStats?.checklistPendingThisMonth} label={t("home.pending_actions")} scope={month} />
              {summary && (
                <>
                  <Kpi value={summary.reportsAwaitingReview} label={t("aces.dash.reportsAwaiting")} scope={t("aces.dash.reportsAwaitingHint")} to="/submittedChecklist" />
                  <Kpi value={summary.analysesAwaitingReview} label={t("aces.dash.analysesAwaiting")} scope={current} to="/ai/analyses" />
                  <Kpi value={summary.openCriticalFindings} label={t("aces.dash.critical")} scope={current} to="/ai/findings" alert />
                  <Kpi value={summary.overdueCapas} label={t("aces.dash.overdue")} scope={current} to="/capa" alert />
                  <Kpi value={summary.capasAwaitingEffectiveness} label={t("aces.dash.effectiveness")} scope={current} to="/capa" />
                </>
              )}
              <Kpi value={allUserStats?.active} label={t("home.active_users")} scope={usersScope} />
              <Kpi value={allUserStats?.total} label={t("home.total_users")} scope={usersScope} />
            </div>

            {summary && (
              <div className="aces-grid-2">
                <div className="aces-card">
                  <h5>{t("aces.dash.alerts")}</h5>
                  {summary.alerts.length === 0 ? <p className="aces-muted mb-0">{t("aces.dash.noAlerts")}</p> : (
                    <ul className="aces-list">
                      {summary.alerts.map((a) => (
                        <li key={`${a.type}-${a.id}`}>
                          <Link className="aces-table-link" to={a.type === "FINDING" ? `/ai/finding/${a.id}` : `/capa/${a.id}`}>{a.ref}</Link>{" "}
                          {a.type === "CAPA"
                            ? t(`aces.dash.capaOverdue_${a.daysOverdue === 1 ? "one" : "other"}`, { count: a.daysOverdue ?? 0 })
                            : a.text}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {summary.recentActivity && (
                  <div className="aces-card">
                    <h5>{t("aces.dash.recent")}</h5>
                    {summary.recentActivity.length === 0 ? <p className="aces-muted mb-0">{t("aces.dash.noRecent")}</p> : (
                      <ul className="aces-list">
                        {summary.recentActivity.map((a) => (
                          <li key={a.id}>
                            <b>{a.entityRef || a.entityType}</b> · {a.action}
                            <div className="aces-muted">{a.userName || "—"} · {fmtDate(a.createdAt)}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
